/* eslint-disable no-restricted-syntax */
import { createFetcher } from '../../Common';

import { queryBuilder } from '../../utils';

import {
  APPLICATIONS_ENDPOINT,
  CAPTCHA_ENDPOINT,
  DUPLICATE_CHECK_ENDPOINT,
  FILE,
  FILE_UPLOAD,
  getBaseUrl,
} from '../../constants';

import { normalizeApplication, normalizeApplications, toApplicationPayload } from '../normalizers';

/**
 * Creates a new Application.
 * @Applications
 */
export class Applications {
  static instance;

  static getInstance() {
    if (!this.instance) {
      this.instance = new Applications(createFetcher());
    }
    return this.instance;
  }

  constructor(fetcher) {
    this.fetcher = fetcher;
    this.endpoint = APPLICATIONS_ENDPOINT;
    this.captcha_endpoint = CAPTCHA_ENDPOINT;
    this.duplicate_check = DUPLICATE_CHECK_ENDPOINT;
    this.file_upload_endpoint = FILE_UPLOAD;
    this.file = FILE;
  }

  get base_url() {
    return getBaseUrl();
  }
  /**
   * this function is to get all applications
   * @function getAllApplications
   ** @param {reqParam} default parameter
   */

  async getAllApplications(reqParams = {}) {
    const normalizedParams = {
      ...reqParams,
      limit: Number(reqParams.limit) > 0 ? Number(reqParams.limit) : 0,
    };

    const queryString = queryBuilder(normalizedParams);
    const url = `${this.base_url}${this.endpoint}${queryString}`;

    const response = await this.fetcher.getData(url);
    return { data: normalizeApplications(response?.data ?? []), meta: response?.meta };
  }

  /**
   * this function is to get application by Id
   * @function getApplicationById
   ** @param {reqId} default parameter
   */

  async getApplicationById(reqId) {
    const url = `${this.base_url + this.endpoint}/${reqId}?newVersion=true`;
    const response = await this.fetcher.getData(url);
    return normalizeApplication(response?.data);
  }
  /**
   * this function is to get resume of a application
   * @function getApplicationResume
   ** @param {reqId} default parameter
   */

  async getApplicationResume(reqId) {
    const url = `${this.base_url + this.endpoint}/${reqId}/resume`;
    const response = await this.fetcher.getData(url);
    return response?.data;
  }

  async getFileUploadURL() {
    const url = `${this.base_url + this.file_upload_endpoint}`;
    const response = await this.fetcher.getData(url);
    return response?.data;
  }
  /**
   * this function is to update application by Id
   * @function putApplicationById
   ** @param {reqParams} default parameter
   */

  async putApplicationById(reqParams) {
    const url = `${this.base_url + this.endpoint}/${reqParams.reqId}`;
    const response = await this.fetcher.putData(url, toApplicationPayload(reqParams.reqBody));
    return normalizeApplication(response?.data);
  }

  /**
   * this function is to update application by Id
   * @function putApplicationReopenById
   ** @param {reqParams} default parameter
   */

  async putApplicationReopenById(reqParams) {
    const url = `${this.base_url + this.endpoint}/${reqParams.reqId}/reopen`;
    const response = await this.fetcher.putData(url, toApplicationPayload(reqParams.reqBody));
    return normalizeApplication(response?.data);
  }

  /**
   * this function is to update application screening by Id
   * @function putApplicationScreeningById
   ** @param {reqParams} default parameter
   */

  async putApplicationScreeningById(reqParams) {
    const url = `${this.base_url + this.endpoint}/${reqParams.reqId}/pre-screening`;
    const response = await this.fetcher.putData(url, toApplicationPayload(reqParams.reqBody));
    return normalizeApplication(response?.data);
  }

  /**
   * this function is to get recaptcha for uploading resume
   * @function getCaptcha
   ** @param {reqId} default parameter
   */

  async getCaptcha() {
    const url = `${this.base_url + this.captcha_endpoint}`;
    const response = await this.fetcher.getData(url);
    return response?.data;
  }

  /**
   * this function is to post application
   * @function postApplication
   ** @param {reqBody} default parameter
   */

  async postApplication(reqBody) {
    const url = `${this.base_url + this.endpoint}`;
    const response = await this.fetcher.postData(url, toApplicationPayload(reqBody));
    return normalizeApplication(response?.data);
  }

  /**
   * sends the consent-and-acknowledgement mail again to applications still waiting for it
   * @function resendConsentMail
   * @param {{ applicationIDs?: number[], positionID?: number, dryRun?: boolean }} reqBody
   * @returns {Promise<{ total: number, sent: number, failed: number, skipped: number, pending: number,
   * results: { applicationID: number, name: string, email: string, status: string, reason?: string }[] }>}
   */

  async resendConsentMail(reqBody) {
    const url = `${this.base_url + this.endpoint}/resend-consent`;
    const response = await this.fetcher.postData(url, reqBody);
    return response?.data;
  }

  /**
   * moves applications waiting at acknowledgment & consent on to the next round their position uses
   * @function overrideConsentRound
   * @param {{ applicationIDs: number[] }} reqBody
   * @returns {Promise<{ total: number, moved: number, failed: number, skipped: number,
   * results: { applicationID: number, name: string, status: string, nextRound?: string, reason?: string }[] }>}
   */

  async overrideConsentRound(reqBody) {
    const url = `${this.base_url + this.endpoint}/override-consent-round`;
    const response = await this.fetcher.postData(url, reqBody);
    return response?.data;
  }

  /**
   * this function is to get application by Id
   * @function getApplicationById
   ** @param {reqId} default parameter
   */

  async getApplicationOfferById(reqId, eventId) {
    const url = `${this.base_url + this.endpoint}/${reqId}/offers?eventID=${eventId}`;
    const response = await this.fetcher.getData(url);
    return response?.data;
  }

  /**
   * this function is to post application
   * @function postApplicationOffer
   ** @param {reqParam} default parameter
   */

  async postApplicationOffer(reqParam) {
    const url = `${this.base_url + this.endpoint}/${reqParam.reqId}/offers`;
    const response = await this.fetcher.postData(url, reqParam.reqBody);
    return response?.data;
  }

  /**
   * this function is to put application
   * @function putApplicationOfferById
   ** @param {reqParam} default parameter
   */

  async putApplicationOfferById(reqParam) {
    const url = `${this.base_url + this.endpoint}/${reqParam.reqId}/offers`;
    const response = await this.fetcher.putData(url, reqParam.reqBody);
    return response?.data;
  }

  /**
   * this function is to check duplicate application
   * @function postDuplicateCheck
   ** @param {reqParam} default parameter
   */

  async postDuplicateCheck(reqBody) {
    const url = `${this.base_url}${this.duplicate_check}`;
    const response = await this.fetcher.postData(url, toApplicationPayload(reqBody));
    return response?.data;
  }

  async fetchResume(applicationID, URL) {
    const url = `${this.base_url + this.endpoint}/${applicationID}${
      this.file
    }?fileURL=${encodeURIComponent(URL)}`;
    const response = await this.fetcher.getData(url);
    return response?.data;
  }
}

export const createApplications = () => Applications.getInstance();
